import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Upload, Edit2, Trash2, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useViewOnly } from '../contexts/ViewOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MILESTONES = [
  { id: 'hospital', title: 'First Day at the Hospital', icon: '🏥' },
  { id: 'first_held', title: 'First Time You Held Your Baby', icon: '👶' },
  { id: 'coming_home', title: 'Your Baby Coming Home', icon: '🏠' },
  { id: 'rolling_over', title: 'Rolling Over', icon: '🔄' },
  { id: 'first_food', title: "Baby's First Solid Food", icon: '🥄' },
  { id: 'first_step', title: "Baby's First Step", icon: '👣' }
];

const Memories = ({ baby }) => {
  const navigate = useNavigate();
  const { viewOnlyMode } = useViewOnly();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    notes: '',
    date_recorded: ''
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRefs = React.useRef({});

  useEffect(() => {
    fetchMemories();
  }, [baby?.id]);

  // Capitalize first letter
  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Format date to "July 15, 2018 . X years X months X days old"
  const formatDisplayDate = (dateString) => {
    if (!dateString || !baby?.date_of_birth) return '';
    
    try {
      // Parse memory date (DD/MM/YYYY format)
      const memoryParts = dateString.split('/');
      if (memoryParts.length !== 3) return dateString;
      
      const memoryDay = parseInt(memoryParts[0]);
      const memoryMonth = parseInt(memoryParts[1]) - 1; // JS months are 0-indexed
      const memoryYear = parseInt(memoryParts[2]);
      const memoryDate = new Date(memoryYear, memoryMonth, memoryDay);
      
      // Format as "July 15, 2018"
      const formattedDate = memoryDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Calculate age from birthday to memory date (same logic as Dashboard)
      let birth;
      const birthDate = baby.date_of_birth;
      
      // Handle DD/MM/YYYY format
      if (birthDate.includes('/')) {
        const [day, month, year] = birthDate.split('/');
        birth = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Handle ISO format (YYYY-MM-DD)
        birth = new Date(birthDate);
      }
      
      let years = memoryDate.getFullYear() - birth.getFullYear();
      let months = memoryDate.getMonth() - birth.getMonth();
      let days = memoryDate.getDate() - birth.getDate();
      
      // Adjust for negative days
      if (days < 0) {
        months--;
        const prevMonth = new Date(memoryDate.getFullYear(), memoryDate.getMonth(), 0);
        days += prevMonth.getDate();
      }
      
      // Adjust for negative months
      if (months < 0) {
        years--;
        months += 12;
      }
      
      // Build age string
      const parts = [];
      if (years > 0) {
        parts.push(`${years} year${years > 1 ? 's' : ''}`);
      }
      if (months > 0) {
        parts.push(`${months} month${months > 1 ? 's' : ''}`);
      }
      if (days > 0 || parts.length === 0) {
        parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      }
      
      const ageString = parts.join(' ');
      
      return `${formattedDate} · ${ageString}`;
    } catch (error) {
      return dateString;
    }
  };

  // Date input handlers (like medical sessions)
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Limit length
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    // Auto-add slashes
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    setFormData(prev => ({ ...prev, date_recorded: value }));
  };

  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const value = formData.date_recorded;
      if (value.length === 3 || value.length === 6) {
        if (value.endsWith('/')) {
          e.preventDefault();
          setFormData(prev => ({ ...prev, date_recorded: value.slice(0, -1) }));
        }
      }
    }
  };

  const fetchMemories = async () => {
    if (!baby?.id) return;
    
    try {
      const response = await axios.get(`${API}/memories/${baby.id}`);
      const memoriesData = response.data.map(memory => {
        // Ensure image URLs are properly constructed
        if (memory.image_url && !memory.image_url.startsWith('http')) {
          // If the URL is relative, make it absolute
          if (memory.image_url.startsWith('/api/')) {
            memory.image_url = `${BACKEND_URL}${memory.image_url}`;
          } else if (memory.image_url.startsWith('/uploads/')) {
            memory.image_url = `${BACKEND_URL}/api${memory.image_url}`;
          }
        }
        return memory;
      });
      setMemories(memoriesData);
    } catch (error) {
      console.error('Error fetching memories:', error);
      toast.error('Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, milestoneId) => {
    if (!file) return;

    // Validate file type (only allow web-compatible formats)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      toast.error('Please upload a web-compatible image format (JPG, PNG, WEBP, or GIF). RAW formats like CR2, NEF are not supported.');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 10MB. Please compress or resize your image.');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // The backend now returns /api/uploads/filename which works with our routing
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      
      // Check if memory exists for this milestone
      const existingMemory = memories.find(m => m.milestone === milestoneId);
      
      if (existingMemory) {
        await axios.put(`${API}/memories/${existingMemory.id}`, {
          baby_id: baby.id,
          milestone: milestoneId,
          image_url: imageUrl,
          notes: existingMemory.notes || '',
          date_recorded: existingMemory.date_recorded || ''
        });
        toast.success('Photo updated!');
      } else {
        await axios.post(`${API}/memories`, {
          baby_id: baby.id,
          milestone: milestoneId,
          image_url: imageUrl,
          notes: '',
          date_recorded: ''
        });
        toast.success('Photo added!');
      }
      
      // Reset file inputs
      const uploadInput = document.getElementById(`upload-${milestoneId}`);
      const changeInput = document.getElementById(`upload-change-${milestoneId}`);
      if (uploadInput) uploadInput.value = '';
      if (changeInput) changeInput.value = '';
      
      fetchMemories();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleEditMemory = (milestone) => {
    const memory = memories.find(m => m.milestone === milestone);
    if (memory) {
      setEditingMemory(memory);
      setFormData({
        image_url: memory.image_url || '',
        notes: memory.notes || '',
        date_recorded: memory.date_recorded || ''
      });
    } else {
      setEditingMemory({ milestone });
      setFormData({ image_url: '', notes: '', date_recorded: '' });
    }
    setShowEditDialog(true);
  };

  const handleSaveMemory = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingMemory.id) {
        await axios.put(`${API}/memories/${editingMemory.id}`, {
          baby_id: baby.id,
          milestone: editingMemory.milestone,
          ...formData
        });
        toast.success('Memory updated!');
      } else {
        await axios.post(`${API}/memories`, {
          baby_id: baby.id,
          milestone: editingMemory.milestone,
          ...formData
        });
        toast.success('Memory added!');
      }

      setShowEditDialog(false);
      fetchMemories();
    } catch (error) {
      console.error('Error saving memory:', error);
      toast.error('Failed to save memory');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    if (!window.confirm('Are you sure you want to delete this memory?')) return;

    try {
      await axios.delete(`${API}/memories/${memoryId}`);
      toast.success('Memory deleted');
      fetchMemories();
    } catch (error) {
      console.error('Error deleting memory:', error);
      toast.error('Failed to delete memory');
    }
  };

  const getMemoryForMilestone = (milestoneId) => {
    return memories.find(m => m.milestone === milestoneId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading memories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Memories</h1>
                <p className="text-blue-200 text-sm">{baby.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Memory Cards - Vertical List */}
        <div className="space-y-6 max-w-md mx-auto">
          {MILESTONES.map((milestone) => {
            const memory = getMemoryForMilestone(milestone.id);
            
            return (
              <div key={milestone.id} className="bg-white rounded-lg shadow-xl overflow-hidden">
                {/* Image Display - Portrait Size */}
                <div className="relative bg-gray-100" style={{ minHeight: '450px', maxHeight: '500px' }}>
                  {memory?.image_url ? (
                    <>
                      <img
                        src={memory.image_url}
                        alt={milestone.title}
                        className="w-full h-full object-contain"
                        style={{ minHeight: '450px', maxHeight: '500px' }}
                        onError={(e) => {
                          console.error('Failed to load memory image:', memory.image_url);
                          // Try to reconstruct the URL if it fails
                          const originalSrc = e.target.src;
                          if (!originalSrc.includes(BACKEND_URL)) {
                            e.target.src = `${BACKEND_URL}/api${memory.image_url.replace(BACKEND_URL, '')}`;
                          }
                        }}
                      />
                      {/* Hover overlay to change photo (similar to profile image) */}
                      {!viewOnlyMode && (
                        <label 
                          htmlFor={`upload-change-${milestone.id}`}
                          className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                            id={`upload-change-${milestone.id}`}
                            className="hidden"
                            onChange={(e) => handleImageUpload(e.target.files[0], milestone.id)}
                            disabled={uploading}
                          />
                          <Camera className="w-12 h-12 text-white mb-2" />
                          <p className="text-white text-sm">{uploading ? 'Uploading...' : 'Change Photo'}</p>
                        </label>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100" style={{ minHeight: '450px', maxHeight: '500px' }}>
                      <Camera className="w-16 h-16 text-gray-400 mb-3" />
                      <p className="text-gray-500 mb-3 text-sm">No photo added yet</p>
                      {!viewOnlyMode && (
                        <>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                            id={`upload-${milestone.id}`}
                            ref={(el) => fileInputRefs.current[`upload-${milestone.id}`] = el}
                            className="hidden"
                            onChange={(e) => handleImageUpload(e.target.files[0], milestone.id)}
                            disabled={uploading}
                          />
                          <Button 
                            disabled={uploading} 
                            size="sm"
                            onClick={() => fileInputRefs.current[`upload-${milestone.id}`]?.click()}
                            type="button"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {uploading ? 'Uploading...' : 'Upload Photo'}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="bg-white p-4 relative">
                  {/* Edit button in top right */}
                  {!viewOnlyMode && (
                    <button
                      onClick={() => handleEditMemory(milestone.id)}
                      className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  )}

                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{capitalizeFirstLetter(baby?.name) || 'Baby'}</h2>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{milestone.title}</h3>
                  
                  {memory?.notes && (
                    <p className="text-gray-600 text-sm mb-3">{memory.notes}</p>
                  )}
                  
                  {memory?.date_recorded && (
                    <p className="text-gray-500 text-sm">
                      {formatDisplayDate(memory.date_recorded)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Edit Memory Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-md z-50">
            <DialogHeader>
              <DialogTitle>Edit Memory Details</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveMemory} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date_recorded">Date</Label>
                <Input
                  id="date_recorded"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={formData.date_recorded}
                  onChange={handleDateInput}
                  onKeyDown={handleDateKeyDown}
                  maxLength={10}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Add your thoughts and memories..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                />
              </div>

              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Memories;
